import { Close } from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';

import { useEffect, useRef } from 'react';
import { Link } from '~/components/Link';
import { waitForRecaptcha } from '~/scripts/recaptcha';
interface RecaptchaDialogProps {
  open: boolean;
  onClose: (token: string) => void;
  onError: (error: Error) => void;
}

export const RecaptchaDialog = ({
  open,
  onClose,
  onError,
}: RecaptchaDialogProps) => {
  const recaptchaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initRecaptcha = async () => {
      if (import.meta.env.VITE_OFFLINE !== '1') {
        await waitForRecaptcha();
      }

      window.grecaptcha?.ready(() => {
        window.grecaptcha?.render(recaptchaRef.current as Element, {
          sitekey: import.meta.env.VITE_RECAPTCHA_V2_KEY!,
          callback: (token: string) => {
            onClose(token);
            window.grecaptcha?.reset();
          },
          'expired-callback': () => {
            onError(new Error('Recaptcha Expired'));
            window.grecaptcha?.reset();
            onClose('');
          },
          'error-callback': (error: Error) => {
            onError(error);
            window.grecaptcha?.reset();
            onClose('');
          },
        });
      });
    };

    if (open) {
      initRecaptcha();
    }
  }, [open, onClose, onError]);

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Verification Required</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => onClose('')}
        sx={() => ({
          position: 'absolute',
          right: 8,
          top: 8,
        })}>
        <Close />
      </IconButton>
      <DialogContent>
        <Typography>
          This check helps prevent unauthorized bots and crawlers. To automate
          your searches or downloads, please use our{' '}
          <Link href="/api">official API</Link>.
        </Typography>
        <Divider sx={{ my: 2 }} />
        <div ref={recaptchaRef} id="recaptcha" />
      </DialogContent>
    </Dialog>
  );
};
